import { useAuth0 } from '@auth0/auth0-react';
import { PencilIcon } from '@heroicons/react/24/outline';
import React, { useState, useEffect } from 'react';

import { useToast } from '../../../context/ToastContext';
import { useUser } from '../../../hooks/useUser';

import UserInfoDisplay from './UserInfoDisplay';
import UserInfoForm from './UserInfoForm';

const UserInfo: React.FC = () => {
  const { user: auth0User, isLoading: auth0Loading } = useAuth0();
  const { user, loading: userLoading, updating, updateUser } = useUser();
  const { showToast } = useToast();

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    businessField: '',
    preferredLanguage: '',
  });

  const isLoading = auth0Loading || userLoading;

  // Initialize form data when user data is loaded
  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        companyName: user.companyName || '',
        businessField: user.businessField || '',
        preferredLanguage: user.preferredLanguage || '',
      });
    }
  }, [user]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updateUser({
        name: formData.name,
        companyName: formData.companyName.trim() || undefined,
        businessField: formData.businessField.trim() || undefined,
        preferredLanguage: formData.preferredLanguage.trim() || undefined,
      });

      showToast(
        'Profile updated',
        'Your profile has been updated successfully.',
        'success'
      );
      setIsEditing(false);
    } catch (err) {
      showToast(
        'Failed to update profile',
        err instanceof Error ? err.message : 'An unexpected error occurred',
        'error'
      );
    }
  };

  const handleCancel = () => {
    if (user) {
      setFormData({
        name: user.name || '',
        companyName: user.companyName || '',
        businessField: user.businessField || '',
        preferredLanguage: user.preferredLanguage || '',
      });
    }
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
        <div className='animate-pulse'>
          <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4'></div>
          <div className='h-20 w-20 bg-gray-200 dark:bg-gray-700 rounded-full mb-4'></div>
          <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2'></div>
          <div className='h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4'></div>
        </div>
      </div>
    );
  }

  if (!auth0User || !user) {
    return (
      <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
        <p className='text-gray-600 dark:text-gray-400'>
          No user information available.
        </p>
      </div>
    );
  }

  return (
    <div className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-6'>
      <div className='flex justify-between items-center mb-4'>
        <h2 className='text-xl font-semibold text-gray-900 dark:text-white'>
          User Information
        </h2>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className='p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-md transition-colors'
            aria-label='Edit profile'
          >
            <PencilIcon className='w-5 h-5' />
          </button>
        )}
      </div>

      <div className='flex flex-col items-center space-y-4'>
        {/* User Avatar */}
        {auth0User.picture && (
          <img
            src={auth0User.picture}
            alt={auth0User.name || 'User avatar'}
            className='w-20 h-20 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700'
          />
        )}

        {isEditing ? (
          <UserInfoForm
            formData={formData}
            auth0Email={auth0User.email || ''}
            updating={updating}
            onFieldChange={(field, value) =>
              setFormData({ ...formData, [field]: value })
            }
            onSave={handleSave}
            onCancel={handleCancel}
          />
        ) : (
          <UserInfoDisplay user={user} auth0User={auth0User} />
        )}
      </div>
    </div>
  );
};

export default UserInfo;
